import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import SystemRoleAssignment from '../SystemRoleAssignment/role_assignment.component';
import { SystemInvoiceApproverPendingApprovals } from './system_invoice_approver_pending_approvals.component';
import { SystemInvoiceApproverAuditLogs } from './system_invoice_approver_audit_logs.component';
import { SystemInvoiceApproverMyPending } from './system_invoice_approver_my_pending.component';
import styles from '../RoleAssignment/role_assignment.module.css';
import {
  hasManageSystemInvoiceApproverAssignmentsAccess,
  hasApproveSystemInvoiceApproverAssignmentsAccess,
  hasSystemInvoiceApproverAssignmentsModuleAccess,
} from '../../common/functions/permission_utils.function';

const parsePermissionNames = () => {
  try {
    const raw = localStorage.getItem('permissions');
    const list = JSON.parse(raw) || [];
    return list.map((p) => p.name);
  } catch {
    return [];
  }
};

const subtitleForPermissions = (hasManage, hasApprove) => {
  if (hasManage && !hasApprove) {
    return 'Changes you submit require manager approval before they take effect.';
  }
  if (hasApprove && !hasManage) {
    return 'Review and approve or reject assignment changes submitted by assigners.';
  }
  if (hasManage && hasApprove) {
    return 'You can assign roles and review pending changes.';
  }
  return '';
};

export const SystemInvoiceApproverAssignmentsMain = () => {
  const navigate = useNavigate();
  const permissionNames = useMemo(() => parsePermissionNames(), []);
  const hasManage = hasManageSystemInvoiceApproverAssignmentsAccess(permissionNames);
  const hasApprove = hasApproveSystemInvoiceApproverAssignmentsAccess(permissionNames);
  const [tab, setTab] = useState(0);
  const [pendingRefreshKey, setPendingRefreshKey] = useState(0);

  useEffect(() => {
    if (!hasSystemInvoiceApproverAssignmentsModuleAccess(permissionNames)) {
      navigate('/rov');
    }
  }, [permissionNames, navigate]);

  const tabs = useMemo(() => {
    const items = [];
    if (hasManage) {
      items.push({ key: 'assign', label: 'Assign roles' });
    }
    if (hasApprove) {
      items.push({ key: 'review', label: 'Review pending' });
    }
    if (hasManage || hasApprove) {
      items.push({ key: 'audit', label: 'Audit log' });
    }
    return items;
  }, [hasManage, hasApprove]);

  const activeKey = tabs[tab]?.key;
  const subtitle = subtitleForPermissions(hasManage, hasApprove);

  const bumpPending = () => setPendingRefreshKey((k) => k + 1);

  if (!hasManage && !hasApprove) {
    return null;
  }

  return (
    <DashboardLayout padding={false}>
      <div className={styles['invoice-approver-assignments-page']}>
        <h1 className={styles['invoice-approver-assignments-page-title']}>
          System Invoice Approver Assignments
        </h1>
        {subtitle && (
          <p className={styles['invoice-approver-assignments-page-subtitle']}>{subtitle}</p>
        )}

        {tabs.length > 1 && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            className={styles['invoice-approver-assignments-tabs']}
            sx={{ mb: 2, borderBottom: '1px solid #E8E9E9' }}
          >
            {tabs.map((t) => (
              <Tab key={t.key} label={t.label} />
            ))}
          </Tabs>
        )}

        {(activeKey === 'assign' || (tabs.length === 1 && hasManage)) && (
          <>
            <SystemRoleAssignment
              submitForApproval
              onSubmissionComplete={bumpPending}
              hidePageHeader
              embedded
              sectionTitle="Role assignments"
            />
            <SystemInvoiceApproverMyPending refreshKey={pendingRefreshKey} />
          </>
        )}

        {(activeKey === 'review' || (tabs.length === 1 && hasApprove && !hasManage)) && (
          <SystemInvoiceApproverPendingApprovals onProcessed={bumpPending} />
        )}

        {activeKey === 'audit' && <SystemInvoiceApproverAuditLogs />}
      </div>
    </DashboardLayout>
  );
};
